/**
 * @fileoverview GET handler for the "/custom_objects/:type_name/:external_id/object_relationships"
 * endpoint. The backend returns the anchor's edges split by direction
 * (`{ outgoing: { hits, results }, incoming: { hits, results } }`); this flattens both into a single
 * table result, tagging each row with its `direction` so the table can render them together and the
 * delete action can rebuild the edge's natural key.
 */
import fetch from "@lib/fetch";

import { reactQueryTableFetcherAdapter } from "../reactQueryFetcherAdapters";
import { customObjectsTypeNameExternalIdObjectRelationships } from "./constants";
import type {
  CustomObjectsTypeNameExternalIdObjectRelationshipsParams,
  CustomObjectsTypeNameExternalIdObjectRelationshipsResult,
  CustomObjectsTypeNameExternalIdObjectRelationshipsResultItem,
  ObjectRelationshipDirection,
  RawCustomObjectsTypeNameExternalIdObjectRelationshipsResponse,
  RawObjectRelationshipRow,
} from "./types";
import { isCustomObjectsTypeNameExternalIdObjectRelationshipsResult } from "./validators";

const toRow = (
  row: RawObjectRelationshipRow,
  direction: ObjectRelationshipDirection
): CustomObjectsTypeNameExternalIdObjectRelationshipsResultItem => ({
  // Row id mirrors the edge's full natural key (direction + related type + rel_kind + external id)
  // so it stays unique: external_id is scoped per type, so two different-type objects can share one.
  id: `${direction}:${row.type_name ?? ""}:${row.rel_kind ?? ""}:${row.external_id}`,
  external_id: row.external_id,
  display_name: row.display_name ?? null,
  type_name: row.type_name ?? null,
  rel_kind: row.rel_kind ?? null,
  relationship_name: row.relationship_name ?? null,
  direction,
});

// Known limitation (acceptable for b2b_ea): the backend paginates each direction independently by the
// same start/limit, so combining them under one summed `hits` means a page can hold up to 2x pageSize
// rows and trailing pages can be empty once both directions have >pageSize edges. EA accounts have few
// relationships (one page in practice); a unified server-paginated endpoint is the follow-up fix.
export const flattenObjectRelationships = (
  response: RawCustomObjectsTypeNameExternalIdObjectRelationshipsResponse
): CustomObjectsTypeNameExternalIdObjectRelationshipsResult => {
  const outgoing = response.outgoing ?? { hits: 0, results: [] };
  const incoming = response.incoming ?? { hits: 0, results: [] };

  return {
    hits: outgoing.hits + incoming.hits,
    results: [
      ...outgoing.results.map((row) => toRow(row, "outgoing")),
      ...incoming.results.map((row) => toRow(row, "incoming")),
    ],
  };
};

export const getDataTableCustomObjectsTypeNameExternalIdObjectRelationships = reactQueryTableFetcherAdapter(
  customObjectsTypeNameExternalIdObjectRelationships,
  (
    signal: AbortSignal,
    {
      appGroupId,
      typeName,
      externalId,
      limit = 12,
      start = 0,
      rel_kind,
    }: CustomObjectsTypeNameExternalIdObjectRelationshipsParams
  ): Promise<CustomObjectsTypeNameExternalIdObjectRelationshipsResult> => {
    return fetch<RawCustomObjectsTypeNameExternalIdObjectRelationshipsResponse>(
      `/custom_objects/${encodeURIComponent(typeName)}/${encodeURIComponent(externalId)}/object_relationships`,
      "GET",
      {
        app_group_id: appGroupId,
        limit,
        start,
        rel_kind,
      },
      { signal },
      { propagateFetchRequestError: false }
    ).then(flattenObjectRelationships);
  },
  isCustomObjectsTypeNameExternalIdObjectRelationshipsResult
);
